import { jobSectionMap } from '../redux/jobSectionMap';
import { normalize } from './helpCore';

export function unscopedUrl(url) {
  if (!url) throw new Error('API is not configured.');
  const parsed = new URL(url, window.location.origin);
  ['id', 'projectid', 'jobid'].forEach((name) => {
    [...parsed.searchParams.keys()].filter((key) => key.toLowerCase() === name)
      .forEach((key) => parsed.searchParams.delete(key));
  });
  return parsed.href;
}
export function listRows(payload) {
  const rows = Array.isArray(payload) ? payload : payload?.data;
  if (!Array.isArray(rows) || rows.some((row) => !row || typeof row !== 'object' || Array.isArray(row))) {
    throw new Error('Unexpected API response; coverage is incomplete.');
  }
  if (!Array.isArray(payload) && (payload.nextLink || payload['@odata.nextLink'] || payload.hasMore ||
    Number(payload.totalCount ?? payload.total) > rows.length)) {
    throw new Error('Paginated response needs backend search support.');
  }
  return rows;
}
const projectId = (row) => row.ProjectID ?? row.ID;
const isVisible = (row) => ![true, 1, '1', 'true'].includes(row.HideFromList);
export function matchJobRows(sections, projects, term, requestedJob) {
  const terms = normalize(term).split(' ').filter(Boolean);
  if (!terms.length) return [];
  const jobs = new Map(projects.filter(isVisible).filter((p) => !requestedJob || String(p.JobNumber) === requestedJob)
    .map((p) => [String(projectId(p)), p]));
  const matches = [];
  for (const [section, rows] of Object.entries(sections)) {
    for (const row of rows) {
      // Child row IDs are not project IDs.
      const id = section === 'generalProject' ? projectId(row) : row.ProjectID;
      const project = jobs.get(String(id));
      if (!project) continue;
      for (const [field, value] of Object.entries(row)) {
        if (value === null || value === undefined || typeof value === 'object' || /^(id|projectid)$/i.test(field)) continue;
        if (!terms.every((t) => normalize(`${field} ${value}`).includes(t))) continue;
        matches.push({ jobId: String(id), jobNumber: project.JobNumber || String(id), section,
          label: field.replace(/([a-z])([A-Z])/g, '$1 $2'), value: String(value), zone: row.Zone });
      }
    }
  }
  return matches;
}
export async function searchJobData({ urls, jobId, term, requestedJob, load, signal }) {
  const sections = {};
  const failed = [];
  let projects;
  if (jobId) {
    const url = new URL(unscopedUrl(urls.fullJob));
    url.searchParams.set('id', jobId);
    const payload = await load(url.href, signal);
    const core = payload?.[jobId] || payload;
    if (!core || typeof core !== 'object' || !('GeneralProjectInfo' in core)) throw new Error('Full-job response is incomplete.');
    for (const [apiKey, section] of Object.entries(jobSectionMap)) {
      if (!(apiKey in core)) { failed.push(section); continue; }
      const raw = core[apiKey];
      const rows = raw === null ? [] : Array.isArray(raw) ? raw : [raw];
      if (rows.some((r) => !r || typeof r !== 'object' || Array.isArray(r))) { failed.push(section); continue; }
      sections[section] = rows.filter((r) => String(section === 'generalProject' ? projectId(r) : (r.ProjectID ?? jobId)) === jobId)
        .map((r) => section === 'generalProject' ? r : { ...r, ProjectID: r.ProjectID ?? jobId });
    }
    projects = sections.generalProject || [];
    if (!projects.length) throw new Error('The requested job was not returned by the API.');
    if (requestedJob && !projects.some((p) => String(p.JobNumber) === requestedJob)) {
      throw new Error(`The question names job ${requestedJob}, but a different job is open. Open that job first.`);
    }
  } else {
    projects = listRows(await load(unscopedUrl(urls.generalProject), signal));
    sections.generalProject = projects;
    // The existing global search uses these list APIs. Limit concurrency and never mutate editable Redux caches.
    const keys = Object.values(jobSectionMap).filter((k) => k !== 'generalProject');
    let next = 0;
    const worker = async () => {
      while (next < keys.length && !signal.aborted) {
        const key = keys[next++];
        if (!urls[key]) { failed.push(key); continue; }
        try { sections[key] = listRows(await load(unscopedUrl(urls[key]), signal)); }
        catch (e) { if (signal.aborted) throw e; failed.push(key); }
      }
    };
    await Promise.all([worker(), worker()]);
  }
  if (signal.aborted) throw new Error('Search cancelled.');
  const matches = matchJobRows(sections, projects, term, requestedJob);
  return { matches: matches.slice(0, 100), total: matches.length, failed, searchedSections: Object.keys(sections).length };
}
